import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
  useRef,
  useMemo
} from 'react';

import { BigNumber, ethers } from 'ethers';
import axios from 'axios';
import BN from 'bn.js';
import moment from 'moment';
import i18next from 'i18next';

import { sleep } from '../utils/utils';
import { tgShowAlert } from '../utils/telegramPopup';
import { getTelegramId } from '../utils/telegramClientData';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'react-interval-hook';
import { DefaultTokenContext } from './DefaultTokenContext';
import useSecureStorage from '../hooks/useSecureStorage';

// WASM 모듈 import
import { initializeWasm } from '../wasm/wasmutils';
import {
  fetch_transaction_data,
  add_server_tx_history,
  get_server_tx_id_list,
  get_server_tx_history
} from '../wasm/rust_wassm_fetch';

// types
import { SYNC_STATUS, SyncTx } from './types/NetworkTypes';


interface BscContextProps {
  networkName: string;
  balance: number;
  countSyncProcess: number;
  countSyncComplete: number;
  syncStatus: string;
  lastSyncTxs: SyncTx[];
  reqTxStop: boolean;

  startDbSyncTimer: () => void;
  stopDbSyncTimer: () => void;

  getBalance: (walletAddress: string) => Promise<number>;
  getBalanceFromServer: (walletAddress: string, coinName: string) => Promise<number>;
  getTokenBalance: (walletAddress: string, mintAddress: string, isFormatting?: boolean, updateType?: string) => Promise<string>;
  getTokenBalanceFromServer: (
    walletAddress: string,
    mintAddress: string,
    coinName: string
  ) => Promise<number>;
  estimateGas: (walletAddress: string) => Promise<string>;
  sendTransactionWithPrivateKey: (
    privateKey: string,
    toAddress: string,
    amount: string,
    tokenSymbol: string,
    tokenAddress: string
  ) => Promise<string>;
  getTotalTxCount: (walletAddress: string) => Promise<any>;
  stopRequestTxData: (isStop?: boolean) => void;
  syncAllTransaction: (walletAddress: string) => Promise<any>;
  getTokenInfo: (mintAddress: string) => Promise<any>;
  getServerTxHistory: (walletAddress: string, network: string, tid: string, tokenName: string, page: number, take: number) => Promise<any>;
  getBlockScanUrl: (txid: string) => Promise<string>;
  isValidAddress: (address: string) => Promise<Boolean>;
}


const defaultContext: BscContextProps = {
  networkName: '',
  balance: 0,
  countSyncProcess: 0,
  countSyncComplete: 0,
  syncStatus: SYNC_STATUS.CHECKING,
  lastSyncTxs: [],
  reqTxStop: false,

  startDbSyncTimer: () => { },
  stopDbSyncTimer: () => { },
  getBalance: async () => 0,
  getBalanceFromServer: async () => Promise.resolve(0),
  getTokenBalance: async () => Promise.resolve('0.0'),
  getTokenBalanceFromServer: async () => Promise.resolve(0),
  estimateGas: async () => '0',
  sendTransactionWithPrivateKey: async () => '',
  getTotalTxCount: async () => Promise.resolve(0),
  stopRequestTxData: () => { },
  syncAllTransaction: async () => Promise.resolve([]),
  getTokenInfo: async () => Promise.resolve(null),
  getServerTxHistory: async () => Promise.resolve([]),
  getBlockScanUrl: async (txid: string) => Promise.resolve(''),
  isValidAddress: async (address: string) => Promise.resolve(false)
};

export const BscContext = createContext<BscContextProps>(defaultContext);

// 일부 캐시 상수
const cacheExpiryTime = 10000;
const gasPriceCacheExpiryTime = 10000;
const reqDelay = 800; // 요청간 지연
const dbTxInterval = 60000; // useInterval 주기
const reqdbTxSyncInterval = 20000; // 최소 20초 간격으로만 syncAllTransaction 가능

const BLOCK_RANGE = 9999; // BSC 제한이 10,000이므로 안전하게 9,999 사용

// ERC-20(BEP-20) 토큰 ABI
const ERC20_ABI = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)'
]

export const BscProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();

  const [networkName, setNetworkName] = useState('bsc');
  const [balance, setBalance] = useState(0);
  const [countSyncProcess, setCountSyncProcess] = useState(0);
  const [countSyncComplete, setCountSyncComplete] = useState(0);
  const [syncStatus, setSyncStatus] = useState(SYNC_STATUS.COMPLETE);
  const [lastSyncTxs, setLastSyncTxs] = useState<SyncTx[]>([]);
  const [wasmInitialized, setWasmInitialized] = useState(false);

  // Solana 코드에서 캐시하던 부분들 재활용
  // const [cache, setCache] = useState<any>({});
  // const [tokenBalanceCache, setTokenBalanceCache] = useState<any>({});
  const [gasPriceCache, setGasPriceCache] = useState<any>({});
  const [reqDbTxInterval, setReqDbTxInterval] = useState(0);

  // 참조
  const [reqTxStop, setReqTxStop] = useState(false);
  const reqTxStopRef = useRef(reqTxStop);
  const [syncKey, setSyncKey] = useState('');
  const syncKeyRef = useRef(syncKey);
  const reqDbTxIntervalRef = useRef(reqDbTxInterval);

  const [isRunAllTransactionHistory, setIsRunAllTransactionHistory] = useState(false);
  const runAllTransactionHistoryRef = useRef(isRunAllTransactionHistory);

  const [isRunLimitTransactionHistory, setIsRunLimitTransactionHistory] = useState(false);
  const runLimitTransactionHistoryRef = useRef(isRunLimitTransactionHistory);

  const { getBlockChain, getWalletAddress } = useSecureStorage();

  // const defaultTokenContext = useContext(DefaultTokenContext);
  // const bscNetwork = defaultTokenContext.searchNetwork('bsc');

  // BSC Provider
  // 페이지 이동시 중복 초기화 방지
  // const provider = React.useMemo(() => {
  //   return new ethers.providers.JsonRpcProvider(process.env.REACT_APP_BSC_RPC_URL!);
  // }, []);

  const defaultTokenContext = useContext(DefaultTokenContext);
  const bscNetwork = defaultTokenContext.searchNetwork('bsc');

  const provider = useMemo(() => {
    console.log(`defaultTokenContext searchNetwork Init : bsc `, bscNetwork)

    // DefaultTokenContext에서 'solana' 네트워크 정보를 가져옴
    if (!bscNetwork || !bscNetwork.rpc_http) {
      console.warn(`BscContext provider is null, waiting for network`);
      return null;
    }

    // 네트워크 정보가 없으면 fallback으로 env 값을 사용
    const rpcUrl = bscNetwork?.rpc_http || '';
    console.log(`BscContext provider bscNetwork rpcUrl: `, rpcUrl)

    // const temp = new ethers.providers.JsonRpcProvider(rpcUrl);
    return new ethers.providers.JsonRpcProvider(rpcUrl);
  }, [bscNetwork]);

  useEffect(() => {
    console.log('BscContext provider changed:', provider);
  }, [provider]);

  // ─────────────────────────────────────────────────────────────
  // useEffect: 초기화 등 (원래 Solana WASM init 등)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (wasmInitialized) return; // 이미 초기화된 경우 재실행 막기

    const initNetwork = async () => {
      try {
        console.warn(`BscContext 웹 어셈블리 init`)
        // WASM init이 필요없다면 제거
        // await init();
        console.warn(`BscContext 웹 어셈블리 init`)
        await initializeWasm();

        setWasmInitialized(true);
      } catch (error) {
        console.error(`BscContext 웹 어셈블리 init error : `, error)
      }
    };
    initNetwork();
  }, [wasmInitialized]);

  // 각 ref 동기화
  useEffect(() => {
    reqTxStopRef.current = reqTxStop;
  }, [reqTxStop]);

  useEffect(() => {
    runAllTransactionHistoryRef.current = isRunAllTransactionHistory;
  }, [isRunAllTransactionHistory]);

  useEffect(() => {
    runLimitTransactionHistoryRef.current = isRunLimitTransactionHistory;
  }, [isRunLimitTransactionHistory]);

  useEffect(() => {
    syncKeyRef.current = syncKey;
  }, [syncKey]);

  useEffect(() => {
    reqDbTxIntervalRef.current = reqDbTxInterval;
  }, [reqDbTxInterval]);

  // ─────────────────────────────────────────────────────────────
  // Timer 예시
  // ─────────────────────────────────────────────────────────────
  const syncDbTxTimer = async () => {
    const walletAddress = await getWalletAddress();
    syncAllTransaction(walletAddress)
      .then(() => {
        console.error(`syncDbTxTimer complete:`);
        stop();
      })
      .catch((error) => {
        console.error(`syncDbTxTimer error:`, error);
        stop();
      });
  };

  const { start, stop, isActive } = useInterval(syncDbTxTimer, dbTxInterval, {
    autoStart: false,
    immediate: true,
    selfCorrecting: true,
    onFinish: () => {
      console.log('useInterval Callback when timer is stopped');
      stop();
    }
  });

  /** DB TX 현행화 타이머 시작 */
  const startDbSyncTimer = async () => {
    const now = Date.now();
    const lastRequestTime = reqDbTxIntervalRef.current;
    if (now - lastRequestTime < reqdbTxSyncInterval) {
      console.warn(`DB TX 현행화: 아직 20초 안 지남`);
      await tgShowAlert(t('balance_screen_message_14'));
      return;
    }
    setReqDbTxInterval(now);

    console.log(`defaultTokenContext startDbSyncTimer provider :  `, provider)

    const walletAddress = await getWalletAddress();
    await syncAllTransaction(walletAddress);
  };

  /** DB TX 현행화 타이머 종료 */
  const stopDbSyncTimer = async () => {
    console.log(`DB TX 현행화 스케줄러 종료`);
    stop();
  };

  /**
   * RPC 에서 BNB 잔액 조회
   * @param walletAddress 
   * @param isFormatting 
   * @returns 
   */
  // const getBalance = async (walletAddress: string, isFormatting = true): Promise<number> => {
  //   try {
  //     const balanceWei = await provider.getBalance(walletAddress);
  //     if (!isFormatting) {
  //       // 숫자로 반환 (eth 단위)
  //       return Number(balanceWei.toString());
  //     }

  //     // 숫자로 반환 (eth 단위)
  //     const balanceEth = parseFloat(ethers.utils.formatEther(balanceWei));
  //     setBalance(Number(balanceEth.toString()));
  //     return Number(balanceEth.toString());
  //   } catch (error) {
  //     console.error(`getBalance error: `, error);
  //     alert('An issue occurred while fetching the coin balance from the rpc server')
  //     return 0;
  //   }
  // };

  const getBalance =
    async (
      walletAddress: string,
      isFormatting = true,
      updateType = 'await'
    ): Promise<number> => {
      try {
        const cacheKey = 'coinbalance_' + bscNetwork?.symbol.toLowerCase();

        let cachedData = 0;

        if ('await' === updateType) {
          const temp = await getBalanceFromRpc(walletAddress)
          localStorage.setItem(cacheKey, temp.toString());
          cachedData = temp;
        } else if ('promise' === updateType) {
          getBalanceFromRpc(walletAddress)
            .then((response) => {
              // 코인 수량 원본 저장
              const temp = response.toString() ? response.toString() : '0';
              localStorage.setItem(cacheKey, temp.toString());
            })
            .catch((error) => { })

          const temp = localStorage.getItem(cacheKey);
          cachedData = Number(temp?.toString() ?? '0')
        } else if ('cache' === updateType) {
          const temp = localStorage.getItem(cacheKey);
          cachedData = Number(temp?.toString() ?? '0');
        } else {
          const temp = localStorage.getItem(cacheKey);
          cachedData = Number(temp?.toString() ?? '0');
        }

        if (!isFormatting) {
          // 숫자로 반환 (eth 단위)
          return Number(cachedData.toString());
        }

        console.log(`getBalance BSC 코인 보유수량 : ${cachedData}`)
        // 숫자로 반환 (eth 단위)
        const balanceEth = parseFloat(ethers.utils.formatEther(cachedData.toString()));
        setBalance(Number(balanceEth.toString()));
        return Number(balanceEth.toString());
      } catch (error) {
        console.error(`getBalance error: `, error);
        alert(t('rpc_req_message_01'))
        return 0;
      }
    };

  const getBalanceFromRpc = async (walletAddress: string): Promise<number> => {
    try {

      if (!provider) {
        console.error('getBalanceFromRpc provider is not initialized');
        return 0;
      }

      const balanceWei = await provider.getBalance(walletAddress);

      // wei 단위 반환
      return Number(balanceWei.toString());
    } catch (error) {
      console.error(`getBalance error: `, error);
      alert(t('rpc_req_message_01'))
      return 0;
    }
  };

  /**
   * 서버에서 BNB 잔액 가져오기
   * @param walletAddress 
   * @param coinName 
   * @param isFormatting 
   * @returns 
   */
  const getBalanceFromServer =
    async (
      walletAddress: string,
      coinName: string,
      isFormatting = true
    ): Promise<number> => {
      if (!walletAddress || !coinName) return 0;

      const network = await getBlockChain();
      const telegramId = await getTelegramId();
      if (!network || !telegramId) {
        console.warn(`getBalanceFromServer: no network or telegramId`);
        return 0;
      }

      try {
        const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balance`, {
          wallet_address: walletAddress,
          network,
          coin_name: coinName,
          tid: telegramId
        });

        if (response.data?.result === true) {
          const rawValue = response.data.data?.ta_total_amount ?? '0';
          const bnWei = ethers.BigNumber.from(rawValue);
          if (!isFormatting) {
            return Number(bnWei.toString());
          }
          const bnb = parseFloat(ethers.utils.formatEther(bnWei));
          return bnb;
        }
        return 0;
      } catch (err) {
        console.error('getBalanceFromServer error:', err);
        alert('An issue occurred while fetching the coin balance from the server')
        return 0;
      }
    };


  /**
   * RPC 에서 토큰 잔액 조회
   * @param walletAddress 
   * @param contractAddress 
   * @param isFormatting 
   * @returns 
   */
  const getTokenBalance = async (
    walletAddress: string,
    contractAddress: string,
    isFormatting = true,
    updateType = 'await'
  ): Promise<string> => {
    try {
      const cacheKey = 'tokenbalance_' + bscNetwork?.symbol.toLowerCase() + '_' + contractAddress;

      let cachedData;

      if ('await' === updateType) {
        const temp = await getTokenBalanceFromRpc(walletAddress, contractAddress);
        const balance = temp.tokenBalance.toString() ? temp.tokenBalance.toString() : '0';
        const decimals = temp.decimals.toString() ? temp.decimals.toString() : '0';
        cachedData = { tokenBalance: balance, decimals: decimals };
        localStorage.setItem(cacheKey, JSON.stringify(cachedData));

      } else if ('promise' === updateType) {
        getTokenBalanceFromRpc(walletAddress, contractAddress)
          .then((response) => {
            // 토큰 수량 원본 저장
            const balance = response.tokenBalance.toString() ? response.tokenBalance.toString() : '0';
            const decimals = response.decimals.toString() ? response.decimals.toString() : '0';
            localStorage.setItem(cacheKey, JSON.stringify({ tokenBalance: balance, decimals: decimals }));
          })
          .catch((error) => { })

        const temp = localStorage.getItem(cacheKey);
        cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
      } else if ('cache' === updateType) {
        const temp = localStorage.getItem(cacheKey);
        cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
      } else {
        const temp = localStorage.getItem(cacheKey);
        cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
      }


      let finalVal = '0';
      if (!isFormatting) {
        finalVal = cachedData.tokenBalance.toString();
      } else {
        finalVal = ethers.utils.formatUnits(cachedData.tokenBalance, cachedData.decimals);
      }

      console.log(`BSC 토큰 ${contractAddress} 보유수량 : ${finalVal}`)

      return finalVal;
    } catch (error) {
      console.error('getTokenBalance error:', error);
      // alert('An issue occurred while fetching the token balance from the rpc server')
      return '0';
    }
  };

  const getTokenBalanceFromRpc = async (
    walletAddress: string,
    contractAddress: string
  ): Promise<{ tokenBalance: number, decimals: number }> => {
    try {
      if (!provider) {
        console.error('provider is not initialized');
        return { tokenBalance: 0, decimals: 0 };
      }

      const contract = new ethers.Contract(contractAddress, ERC20_ABI, provider);

      const [rawBalance, decimals] = await Promise.all([
        contract.balanceOf(walletAddress),
        contract.decimals()
      ]);

      return { tokenBalance: rawBalance, decimals: decimals };
    } catch (error) {
      console.error('getTokenBalance error:', error);
      // alert('An issue occurred while fetching the token balance from the rpc server')
      return { tokenBalance: 0, decimals: 0 };
    }
  };


  /**
   * 서버에서 토큰 잔액 가져오기
   * @param walletAddress 
   * @param mintAddress 
   * @param coinName 
   * @param isFormatting 
   * @returns 
   */
  const getTokenBalanceFromServer = async (
    walletAddress: string,
    mintAddress: string,
    coinName: string,
    isFormatting = true
  ): Promise<number> => {
    if (!walletAddress || !coinName || !mintAddress) return 0;

    const network = await getBlockChain();
    const telegramId = await getTelegramId();
    if (!network || !telegramId) {
      console.warn(`getTokenBalanceFromServer: no network or telegramId`);
      return 0;
    }

    if (!provider) {
      console.error('provider is not initialized');
      return 0;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balance`, {
        wallet_address: walletAddress,
        network,
        coin_name: coinName,
        tid: telegramId
      });

      if (response.data?.result === true) {
        const rawVal = ethers.BigNumber.from(response.data.data?.ta_total_amount ?? '0');
        // 컨트랙트 decimals
        const contract = new ethers.Contract(mintAddress, ERC20_ABI, provider);
        const decimals: number = await contract.decimals();
        if (!isFormatting) {
          return Number(rawVal.toString());
        }
        const finalVal = parseFloat(ethers.utils.formatUnits(rawVal, decimals));
        return finalVal;
      }
      return 0;
    } catch (error) {
      console.error('getTokenBalanceFromServer error:', error);
      alert('An issue occurred while fetching the token balance from the server')
      return 0;
    }

  };

  /**
   * 서버 코인/토큰 잔액 조회
   * @param walletAddress 
   * @returns 
   */
  const getBalanceListFromServer = async (walletAddress: string): Promise<any> => {
    // 파라미터 유효성 검사
    if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
      console.error('유효하지 않은 지갑 주소입니다.');
      return null;
    }

    const network = await getBlockChain()
    const telegramId = await getTelegramId()

    try {
      const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balancelist`, {
        wallet_address: walletAddress,
        network: network,
        coin_name: '',
        tid: telegramId
      });

      if (response.data.data && response.data.result === true) {
        console.log(`서버에서 받은 보유수량 리스트 :`, response.data.data)
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('서버에서 보유수량 리스트 조회 중 오류 발생:', error);
      alert('An issue occurred while fetching the balance list from the server')
      return null;
    }
  }


  /**
   * 추정 가스비
   * @param walletAddress 
   * @returns 
   */
  const estimateGas = async (walletAddress: string): Promise<string> => {
    try {
      // 간단히 transfer(0)로 가스비 예측
      const currentTime = Date.now();
      const cachedData = gasPriceCache['gasPrice'];

      if (cachedData && currentTime - cachedData.timestamp < gasPriceCacheExpiryTime) {
        console.log('Using cached gas price');
        return cachedData.gasFee;
      }

      if (!provider) {
        console.error('provider is not initialized');
        return '0';
      }

      const sampleTx = {
        from: walletAddress,
        to: walletAddress,
        value: ethers.utils.parseEther('0')
      };

      const gasEstimate = await provider.estimateGas(sampleTx);
      const gasPrice = await provider.getGasPrice();
      const feeWei = gasEstimate.mul(gasPrice);
      const feeEth = ethers.utils.formatEther(feeWei);

      gasPriceCache['gasPrice'] = {
        gasFee: feeEth,
        timestamp: currentTime
      };
      setGasPriceCache(gasPriceCache);
      return feeEth;
    } catch (error) {
      console.error('Error estimateGas :', error);
      alert('An issue occurred while estimating the gas fee')
      return '0';
    }

  };

  // ─────────────────────────────────────────────────────────────
  // 5) BNB or ERC-20 전송
  // ─────────────────────────────────────────────────────────────
  const sendTransactionWithPrivateKey = async (
    privateKey: string,
    toAddress: string,
    amount: string,
    tokenSymbol: string,
    tokenAddress: string = ''
  ): Promise<string> => {

    if (Number(amount) < Number(process.env.REACT_APP_BSC_SEND_MIN_AMOUNT)) {
      console.error(`전송 최소 수량인 ${process.env.REACT_APP_BSC_SEND_MIN_AMOUNT} 이하 전송 : ${amount}`);
      const alertMessage = t('template_withdraw_screen_message_36') + ' ' + process.env.REACT_APP_BSC_SEND_MIN_AMOUNT;
      alert(alertMessage);
      return '';
    }

    try {
      const mainCurrency = bscNetwork?.currency || 'bnb'
      if (tokenSymbol.toLowerCase() === (mainCurrency || 'ton').toLowerCase()) {
        // BNB 전송
        return await sendBnbCoin(privateKey, toAddress, amount);
      } else {
        // "SPL 토큰" -> 실제로는 ERC-20
        return await sendBepTokenV1(privateKey, toAddress, amount, tokenAddress);
      }
    } catch (error) {
      console.error('BSC sendTransactionWithPrivateKey error:', error);
      alert('An issue occurred while sending the transaction')
      throw error;
    }
  };


  // BNB 전송
  const sendBnbCoin = async (
    privateKey: string,
    toAddress: string,
    amount: string
  ): Promise<string> => {

    if (!provider) {
      console.error('provider is not initialized');
      return '';
    }

    try {
      const wallet = new ethers.Wallet(privateKey, provider);
      const tx = await wallet.sendTransaction({
        to: toAddress,
        value: ethers.utils.parseEther(amount),
        gasLimit: ethers.utils.hexlify(21000)
      });
      const receipt = await tx.wait();
      return receipt.transactionHash;
    } catch (error) {
      console.error('sendBnbCoin error:', error);
      alert('An issue occurred while sending the BNB coin transaction')
      return '';
    }
  };

  // 원래는 sendSplTokenV1(민트 주소...) → 여기서는 ERC-20 전송
  const sendBepTokenV1 = async (
    fromSecretKey: string,
    toAddress: string,
    amount: string,
    mintAddress: string
  ): Promise<string> => {

    if (!provider) {
      console.error('provider is not initialized');
      return '';
    }

    try {
      const wallet = new ethers.Wallet(fromSecretKey, provider);
      const contract = new ethers.Contract(mintAddress, ERC20_ABI, wallet);
      const decimals = await contract.decimals();
      const amountBN = ethers.utils.parseUnits(amount, decimals);
      const tx = await contract.transfer(toAddress, amountBN);
      const receipt = await tx.wait();
      return receipt.transactionHash;
    } catch (error) {
      console.error('sendBepTokenV1 (ERC-20) error:', error);
      alert('An issue occurred while sending the token transaction')
      return '';
    }
  };


  /**
 * BSC 네트워크에서 네이티브 트랜잭션(일반+내부)을 모두 조회
 * @param walletAddress
 * @param order 정렬 asc / desc (기본값 asc)
 * @returns { signature: string, slot: number }[]
 */
  const getAllNativeTxIdForAddressFromBscScan = async (
    walletAddress: string,
    order: string = 'asc',
    waitTime = 700,
  ): Promise<{
    signature: string;
    slot: number;
    mintAddress: string;
    txType: 'normal' | 'internal';
    hash?: string;
    from?: string;
    to?: string;
    value?: string;
    timeStamp?: string;
    nonce?: string;
    blockHash?: string;
    transactionIndex?: string;
    gas?: string;
    gasPrice?: string;
    isError?: string;
    txreceipt_status?: string;
    input?: string;
    contractAddress?: string;
    cumulativeGasUsed?: string;
    gasUsed?: string;
    confirmations?: string;
    methodId?: string;
    functionName?: string;
    traceId?: string;      // internal tx 전용 필드
    type?: string;         // internal tx에서 "call" 등
    errCode?: string;      // internal tx에서 에러코드
  }[]> => {
    try {
      console.log(`getAllNativeTxIdForAddressFromBscScan provider: `, provider)

      if (!provider) {
        console.error('provider is not initialized');
        return [];
      }

      // 1) 최신 블록 번호 조회
      const latestBlock = await provider.getBlockNumber();
      console.log(`1 getAllNativeTxIdForAddressFromBscScan latestBlock: ${latestBlock}`);

      // 2) BscScan URL
      const apiUrl = bscNetwork?.scan || '';

      let allTxs: any[] = []; // 일반 + 내부 트랜잭션 모두 담을 배열
      let currentBlock = latestBlock;
      const blockRangeSize = 10000000;

      // 3) 블록 범위를 [endBlock ~ startBlock]씩 내려가며 루프
      while (currentBlock > 0) {
        if (reqTxStopRef.current) {
          console.log('getAllNativeTxIdForAddressFromBscScan 트랜잭션 조회가 중단되었습니다.');
          break;
        }

        const startBlock = Math.max(0, currentBlock - blockRangeSize);
        const endBlock = currentBlock;

        // 4) 일반 트랜잭션(txlist) + 내부 트랜잭션(txlistinternal) 동시 호출
        const [normalTxResponse, internalTxResponse] = await Promise.all([
          axios.get(`${apiUrl}`, {
            params: {
              module: 'account',
              action: 'txlist',
              address: walletAddress,
              startblock: startBlock,
              endblock: endBlock,
              sort: order // 정렬순서
            }
          }),
          axios.get(`${apiUrl}`, {
            params: {
              module: 'account',
              action: 'txlistinternal',
              address: walletAddress,
              startblock: startBlock,
              endblock: endBlock,
              sort: order // 정렬순서
            }
          })
        ]);

        // 5) 일반 트랜잭션 결과
        if (normalTxResponse.data.status === '1') {
          // "txType=normal" 필드를 추가
          const normalTxs = normalTxResponse.data.result.map((tx: any) => ({
            ...tx,
            txType: 'normal',
          }));
          allTxs = [...allTxs, ...normalTxs];
        }

        // 6) 내부 트랜잭션 결과
        if (internalTxResponse.data.status === '1') {
          // "txType=internal" 필드를 추가
          const internalTxs = internalTxResponse.data.result.map((tx: any) => ({
            ...tx,
            txType: 'internal',
          }));
          allTxs = [...allTxs, ...internalTxs];
        }

        console.log(`2 getAllNativeTxIdForAddressFromBscScan allTxs:`, allTxs)

        // API 호출 간 딜레이 (rate limit 방지)
        await sleep(waitTime);

        // 다음 루프: [startBlock .. startBlock - blockRangeSize]
        currentBlock = startBlock;

        // 마지막 블럭까지 진행 완료
        if (currentBlock <= 0) {
          console.log('마지막 블럭까지 처리 완료');
          break;
        }
      }

      // 7) 중복 제거 (tx.hash 기준)
      // 내부 트랜잭션은 'hash'가 같더라도 distinct records 가능하나,
      // signature=tx.hash 로 사용하므로 일단 hash가 같으면 중복으로 처리
      // const uniqueTxs = Array.from(
      //   new Map(allTxs.map((tx) => [tx.hash + tx.timeStamp + tx.contractAddress, tx])).values()
      // );
      // console.log(`3 getAllNativeTxIdForAddressFromBscScan uniqueTxs:`, uniqueTxs)
      // ↑ 만약 internal tx는 hash는 같아도 contractAddress가 다를 수 있어
      //   더 구분하기 위해서 key를 hash+contractAddress 등으로 구성

      // 8) 정렬
      //   slot = Number(tx.blockNumber)
      //   "asc" => 작은 blockNumber 먼저
      //   "desc" => 큰 blockNumber 먼저
      // let sortedTxs: any[];
      // if (order === 'asc') {
      //   sortedTxs = uniqueTxs.sort((a, b) => Number(a.blockNumber) - Number(b.blockNumber));
      // } else {
      //   sortedTxs = uniqueTxs.sort((a, b) => Number(b.blockNumber) - Number(a.blockNumber));
      // }
      // console.log(`4 getAllNativeTxIdForAddressFromBscScan sortedTxs:`, sortedTxs)

      // 8) 중복제거 하지 않고 정렬
      // 같은 tx hash 내부에서 코인, 토큰이 동시에 변경되는 경우가 있을 수 있음
      let sortedTxs: any[];
      if (order === 'asc') {
        sortedTxs = allTxs.sort((a, b) => Number(a.blockNumber) - Number(b.blockNumber));
      } else {
        sortedTxs = allTxs.sort((a, b) => Number(b.blockNumber) - Number(a.blockNumber));
      }
      console.log(`4 getAllNativeTxIdForAddressFromBscScan sortedTxs:`, sortedTxs)

      // 9) 변환
      // sortedTxs의 각 요소를 1:1로 매핑하므로 결과 배열의 길이는 동일합니다
      let txIdList: any[] = sortedTxs.map((tx) => ({
        signature: tx.hash,
        slot: Number(tx.blockNumber),
        mintAddress: '', // native tx는 mintAddress가 없음
        txType: tx.txType,

        // normal tx 필드
        hash: tx.hash,
        from: tx.from,
        to: tx.to,
        value: tx.value,
        timeStamp: tx.timeStamp,
        nonce: tx.nonce,
        blockHash: tx.blockHash,
        transactionIndex: tx.transactionIndex,
        gas: tx.gas,
        gasPrice: tx.gasPrice,
        isError: tx.isError,
        txreceipt_status: tx.txreceipt_status,
        input: tx.input,
        contractAddress: tx.contractAddress,
        cumulativeGasUsed: tx.cumulativeGasUsed,
        gasUsed: tx.gasUsed,
        confirmations: tx.confirmations,
        methodId: tx.methodId,
        functionName: tx.functionName,

        // internal tx 필드
        traceId: tx.traceId, // e.g. "0_1"
        type: tx.type,       // e.g. "call"
        errCode: tx.errCode, // e.g. ""
      }));
      // txIdList.length === sortedTxs.length가 보장됩니다

      console.log(`5 getAllNativeTxIdForAddressFromBscScan 전체 tx 수(일반+내부): ${txIdList.length}`, txIdList);
      return txIdList;
    } catch (error) {
      console.error('getAllNativeTxIdForAddressFromBscScan error:', error);
      return [];
    }
  };


  /**
   * RPC 에서 지정 지갑주소의 지정한 토큰(BEP20) 트랜잭션(Transfer 이벤트) 조회
   * @param walletAddress 지갑주소
   * @param contractAddress BEP20 토큰 컨트랙트 주소
   * @param order 'asc' | 'desc' 정렬순서
   * @returns {signature, slot}[] (slot은 blockNumber)
   */
  // const getAllBEP20TxIdForAddressFromRpc = async (
  //   walletAddress: string,
  //   contractAddress: string,
  //   order: string = 'asc'
  // ): Promise<{ signature: string; slot: number }[]> => {
  //   try {
  //     console.log(`BscContext getAllBEP20TxIdForAddress walletAddress:${walletAddress} contractAddress:${contractAddress} order:${order}`);

  //     // 1) BEP20 컨트랙트 확인
  //     const contract = new ethers.Contract(
  //       contractAddress,
  //       [
  //         'event Transfer(address indexed from, address indexed to, uint value)',
  //         'function decimals() view returns (uint8)'
  //       ],
  //       provider
  //     );

  //     // 2) "from=walletAddress" 필터, "to=walletAddress" 필터
  //     const filterFrom = contract.filters.Transfer(walletAddress, null);
  //     const filterTo = contract.filters.Transfer(null, walletAddress);

  //     // 3) decimals, logsFrom, logsTo 병렬 조회 (페이지네이션 적용)
  //     const [decimals, logsFrom, logsTo] = await Promise.all([
  //       contract.decimals(),
  //       getLogsInBatches(provider, filterFrom, reqTxStop),
  //       getLogsInBatches(provider, filterTo, reqTxStop)
  //     ]);

  //     // 4) logs를 담을 배열 / 중복 제거를 위한 Set
  //     const uniqueTransactions = new Set<string>();
  //     const allTransfers: any[] = [];

  //     // 5) 각 log를 파싱
  //     const processLog = async (log: any) => {
  //       if (reqTxStopRef.current) {
  //         return;
  //       }
  //       // parseLog
  //       const parsed = contract.interface.parseLog(log);
  //       // 블록 정보
  //       const block = await provider.getBlock(log.blockNumber);

  //       // 실제 전송값을 decimals로 변환
  //       const valueWei = parsed.args.value;
  //       const value = ethers.utils.formatUnits(valueWei, decimals);

  //       const transaction = {
  //         hash: log.transactionHash,
  //         from: parsed.args.from,
  //         to: parsed.args.to,
  //         value, // string
  //         timestamp: block.timestamp, // number(초 단위)
  //         blockNumber: log.blockNumber
  //       };

  //       // 중복 검사 (hash + blockNumber 등 조합)
  //       const txKey = transaction.hash + transaction.blockNumber;
  //       if (!uniqueTransactions.has(txKey)) {
  //         uniqueTransactions.add(txKey);
  //         allTransfers.push(transaction);
  //       }
  //     };

  //     // 6) logsFrom, logsTo 각각 처리
  //     await Promise.all(logsFrom.map(processLog));
  //     await Promise.all(logsTo.map(processLog));

  //     // 7) 정렬 (order= 'asc'|'desc')
  //     if (order === 'asc') {
  //       allTransfers.sort((a, b) => a.blockNumber - b.blockNumber);
  //     } else {
  //       // desc
  //       allTransfers.sort((a, b) => b.blockNumber - a.blockNumber);
  //     }

  //     // 8) 최종 변환 => { signature, slot }
  //     // slot = blockNumber, signature = hash
  //     const txIdList = allTransfers.map((tx) => ({
  //       signature: tx.hash,
  //       slot: tx.blockNumber
  //     }));

  //     console.log('getAllBEP20TxIdForAddress txIdList:', txIdList);

  //     return txIdList;
  //   } catch (error) {
  //     console.error('getAllBEP20TxIdForAddress error:', error);
  //     return [];
  //   }
  // };

  /**
   * getLogs 검색 요청
   * 한번에 요청 가능한 블록 범위 10000개 제한으로 루프 돌며 분할해서 조회
   * @param provider 
   * @param filter 
   * @param reqTxStop 
   * @returns 
   */
  const getLogsInBatches = async (
    provider: ethers.providers.Provider,
    filter: any,
    reqTxStop?: boolean
  ): Promise<any[]> => {
    const logs = [];
    const latestBlock = await provider.getBlockNumber();
    let fromBlock = latestBlock; // 최신 블록부터 시작
    let toBlock = fromBlock;
    let endBlock = 0; // 0번 블록까지 검색

    while (fromBlock > endBlock) {
      if (reqTxStopRef.current) {
        console.log('getLogsInBatches 트랜잭션 조회가 중단되었습니다.');
        break;
      }


      try {
        // fromBlock에서 BLOCK_RANGE만큼 과거로 검색
        toBlock = fromBlock;
        fromBlock = Math.max(endBlock, toBlock - BLOCK_RANGE);

        const batchLogs = await provider.getLogs({
          ...filter,
          fromBlock,
          toBlock
        });

        console.log(`BscContext getLogsInBatches batchLogs fromBlock:${fromBlock} toBlock:${toBlock} : `, batchLogs)
        logs.push(...batchLogs);

        // 요청 간 지연
        await sleep(700);

      } catch (error) {
        console.error(`Error fetching logs for block range ${fromBlock}-${toBlock}:`, error);
        throw error;
      }
    }

    console.log(`BscContext getLogsInBatches logs count:${logs.length} : `, logs)

    return logs;
  };

  /**
 * BscScan (Testnet) API를 통해 특정 walletAddress + BEP-20 contractAddress의 트랜잭션 정보를 모두 가져온다.
 * @param walletAddress 지갑 주소 (0x...)
 * @param contractAddress BEP-20 토큰 컨트랙트 주소 (0x...)
 * @param order 결과 정렬 (asc | desc), 기본 'asc'
 * @returns { signature: string; slot: number }[] (signature=트랜잭션 해시, slot=블록 번호)
 */
  const getAllBEP20TxIdForAddressFromBscScan = async (
    walletAddress: string,
    contractAddress: string,
    order: string = 'asc'
  ): Promise<{
    signature: string;
    slot: number;
    mintAddress: string
    txType: 'normal' | 'internal';

    // 추가 필드
    hash?: string;
    timeStamp?: string;
    nonce?: string;
    blockHash?: string;
    from?: string;
    to?: string;
    value?: string;
    tokenName?: string;
    tokenSymbol?: string;
    tokenDecimal?: string;
    transactionIndex?: string;
    gas?: string;
    gasPrice?: string;
    gasUsed?: string;
    cumulativeGasUsed?: string;
    input?: string;
    confirmations?: string;
  }[]> => {
    try {

      // 1) BscScan URL
      const apiUrl = bscNetwork?.scan || '';

      // 페이지 / offset 설정
      // offset=5는 예시이며, 트랜잭션이 많을 수록 조금 크게 잡아도 됩니다.
      let page = 1;
      const offset = 5;

      let results: {
        signature: string;
        slot: number;
        mintAddress: string;
        txType: 'normal' | 'internal';
        hash?: string;
        timeStamp?: string;
        nonce?: string;
        blockHash?: string;
        from?: string;
        to?: string;
        value?: string;
        tokenName?: string;
        tokenSymbol?: string;
        tokenDecimal?: string;
        transactionIndex?: string;
        gas?: string;
        gasPrice?: string;
        gasUsed?: string;
        cumulativeGasUsed?: string;
        input?: string;
        confirmations?: string;
      }[] = [];

      let hasMore = true;

      while (hasMore) {
        // API 요청 URL 구성
        const response = await axios.get(`${apiUrl}`, {
          params: {
            module: 'account',
            action: 'tokentx',
            contractaddress: contractAddress,
            address: walletAddress,
            page: page,
            offset: offset,
            sort: order
          }
        });

        console.log(`getAllBEP20TxIdForAddressFromBscScan BscScan API 응답`, response);

        // HTTP GET 요청
        const data = response.data;

        // BscScan API의 status가 '1'이면 성공, '0'이면 실패/데이터없음
        if (!data || data.status !== '1' || !Array.isArray(data.result)) {
          console.warn(`BscScan API 응답이 정상이 아님 : status=${data?.status}, message=${data?.message}`);
          break;
        }

        const txList = data.result;
        if (txList.length === 0) {
          // 더 이상 결과가 없으면 종료
          hasMore = false;
          break;
        }

        // 필요한 정보만 변환해서 results에 누적
        for (const tx of txList) {
          results.push({
            signature: tx.hash,
            slot: Number(tx.blockNumber),
            mintAddress: contractAddress,
            txType: 'normal', // 추가된 부분

            // 추가 필드  
            hash: tx.hash,
            timeStamp: tx.timeStamp,
            nonce: tx.nonce,
            blockHash: tx.blockHash,
            from: tx.from,
            to: tx.to,
            value: tx.value,
            tokenName: tx.tokenName,
            tokenSymbol: tx.tokenSymbol,
            tokenDecimal: tx.tokenDecimal,
            transactionIndex: tx.transactionIndex,
            gas: tx.gas,
            gasPrice: tx.gasPrice,
            gasUsed: tx.gasUsed,
            cumulativeGasUsed: tx.cumulativeGasUsed,
            input: tx.input,
            confirmations: tx.confirmations
          });
        }

        // 이번에 가져온 건수가 offset보다 작으면 마지막 페이지임
        if (txList.length < offset) {
          hasMore = false;
        } else {
          page += 1; // 다음 페이지
        }
      }

      return results;
    } catch (error) {
      console.error('getAllBEP20TxIdForAddressFromRpc error:', error);
      return [];
    }
  };

  /**
   * RPC 서버 트랜잭션 조회 요청
   * @param txid 
   * @param retries 
   * @returns 
   */
  const fetchTransactionRpc = async (
    txid: string,
    waitTime = 700,
    retries = 3
  ) => {

    if (!provider) {
      console.error('provider is not initialized');
      return null;
    }

    try {
      // txid가 null이거나 undefined인 경우 체크
      if (!txid) {
        console.error('Error: txid가 null 또는 undefined 입니다');
        return null;
      }

      // txid 형식 체크 (0x로 시작하는 66자리 16진수)
      const txidRegex = /^0x[0-9a-fA-F]{64}$/;
      if (!txidRegex.test(txid)) {
        console.error('Error: 올바르지 않은 txid 형식입니다');
        return null;
      }

      const transaction = await provider.getTransactionReceipt(txid);
      await sleep(waitTime);
      console.log(`---------------------------1 fetchTransactionRpc getTransaction : `, transaction)

      return transaction;
    } catch (error) {
      console.error('Error 웹어셈블리 fetching transaction:', error);
    }
    return null;
  }

  const fetchParsedTransactionRpc = async (
    txid: string,
    waitTime = 700
  ) => {

    if (!provider) {
      console.error('provider is not initialized');
      return null;
    }

    try {
      // txid가 null이거나 undefined인 경우 체크
      if (!txid) {
        console.error('Error: txid가 null 또는 undefined 입니다');
        return null;
      }

      // txid 형식 체크 (0x로 시작하는 66자리 16진수)
      const txidRegex = /^0x[0-9a-fA-F]{64}$/;
      if (!txidRegex.test(txid)) {
        console.error('Error: 올바르지 않은 txid 형식입니다');
        return null;
      }

      const tx = await provider.getTransaction(txid);
      await sleep(waitTime);

      const receipt = await provider.getTransactionReceipt(txid);
      await sleep(waitTime);

      // 컨트랙트 인터페이스가 있는 경우
      const iface = new ethers.utils.Interface(ERC20_ABI);

      let parsedData: {
        hash: string;
        from: string;
        to: string | undefined;
        value: string;
        blockNumber: number | undefined;
        timestamp: number;
        status: string;
        gasUsed: string;
        effectiveGasPrice: string;
        decodedInput?: any;
        data?: string;
        events?: any[];
      } = {
        hash: tx.hash,
        from: tx.from,
        to: tx.to,
        value: ethers.utils.formatEther(tx.value),
        blockNumber: tx.blockNumber,
        timestamp: tx.blockNumber ? (await provider.getBlock(tx.blockNumber)).timestamp : 0,
        status: receipt.status === 1 ? 'Success' : 'Failed',
        gasUsed: receipt.gasUsed.toString(),
        effectiveGasPrice: receipt.effectiveGasPrice.toString()
      };

      // 컨트랙트 호출인 경우 input 데이터 디코딩 시도
      if (tx.data !== '0x') {
        try {
          parsedData.decodedInput = iface.parseTransaction({ data: tx.data });
        } catch (e) {
          parsedData.data = tx.data;
        }
      }

      // 이벤트 로그 파싱
      if (receipt.logs.length > 0) {
        parsedData.events = receipt.logs.map(log => {
          try {
            return iface.parseLog(log);
          } catch (e) {
            return log;
          }
        });
      }

      return parsedData;
    } catch (error) {
      console.error('Error 웹어셈블리 fetching transaction:', error);
    }

    return null;
  }

  /**
   * 웹어셈블리 트랜잭션 조회 요청
   * @param signature 
   * @param retries 
   * @returns 
   */
  const fetchTransactionWasm = async (
    signature: string,
    waitTime = 700
  ) => {
    try {
      // WASM 함수 호출
      const rpcUrl = bscNetwork?.rpc_http || '';
      const transaction = await fetch_transaction_data(signature, rpcUrl);
      await sleep(waitTime);

      // result은 JSON 문자열이므로 파싱
      const parsedData = await JSON.parse(transaction);
      const result = parsedData.result;

      return result;
    } catch (error) {
      console.error('Error 웹어셈블리 fetching transaction:', error);
    }

    return null;
  };
  /**
   * 지갑주소 총 트랜잭션 수 조회
   * @param walletAddress 
   * @returns 
   */
  const getTotalTxCount = async (walletAddress: string) => {

    if (!provider) {
      console.error('provider is not initialized');
      return null;
    }

    try {
      // 지갑의 아웃바운드 txCount (nonce)만 반환
      const txCount = await provider.getTransactionCount(walletAddress);
      return txCount;
    } catch (error) {
      console.error('getTotalTxCount error:', error);
      return 0;
    }
  };

  /**
   * 메인지갑 트랜잭션 수 조회
   * @param walletAddress 
   * @param limit 
   * @returns 
   */
  // const getLimitTxCount = async (walletAddress: string, limit: number = 1000) => {
  //   try {
  //     // 단순히 getTxIdForAddress(..., limit) 호출 후 길이 반환
  //     const txList = await getAllNativeTxIdForAddress(walletAddress, 'asc');
  //     return txList.length;
  //   } catch (error) {
  //     console.error('getLimitTxCount error:', error);
  //     return 0;
  //   }
  // };

  /**
   * 로컬스토리지에 저장된 네트워크 토큰 데이터 조회
   * @param forceUpdate 
   * @returns 
   */
  const getNetworkTokenData = async (network: string): Promise<any> => {
    const tokenDataKey = 'tokenData';
    const tokenInfo = localStorage.getItem(tokenDataKey);

    let networkTokens = [];
    if (tokenInfo && tokenInfo !== null && tokenInfo !== undefined && tokenInfo !== '') {
      networkTokens = JSON.parse(tokenInfo).filter((token: any) => {
        return (token.networkname.toLowerCase() === network.toLowerCase()
          && token.type === 'token'
          && token.contract && token.contract !== '');
      });
    }
    console.log(`BscContext getNetworkTokenData network:${network} count:${networkTokens.length}`, networkTokens);

    return networkTokens;
  };

  /**
   * 토큰 주소로 로컬 스토리지의 네트워크 토큰 정보 검색
   */
  const getNetworkTokenDataByAddress = async (
    network: string,
    tokenAddress: string
  ): Promise<any> => {
    const tokenDataKey = 'tokenData';
    const tokenInfo = localStorage.getItem(tokenDataKey);

    let networkToken = null;
    if (tokenInfo && tokenInfo !== null && tokenInfo !== undefined && tokenInfo !== '') {
      const networkTokens = JSON.parse(tokenInfo).filter((token: any) => {
        return (token.networkname.toLowerCase() === network.toLowerCase()
          && token.type === 'token'
          && token.contract && token.contract.toLowerCase() === tokenAddress.toLowerCase());
      });
      if (networkTokens.length > 0) {
        networkToken = networkTokens[0];
      }
    }
    console.log(`BscContext getNetworkTokenDataByAddress network:${network} count:${networkToken ? 1 : 0}`, networkToken);

    return networkToken;
  };

  // 중지 flag
  const stopRequestTxData = useCallback((isStop: boolean = true) => {
    console.log('stopRequestTxData =>', isStop);
    setReqTxStop(isStop);
  }, []);

  /**
   * 백그라운드 DB 현행화
   * @param walletAddress 
   * @param limit 
   * @returns 
  **/
  const syncAllTransaction = async (walletAddress: string) => {
    if (runAllTransactionHistoryRef.current) {
      console.warn('이미 syncAllTransactionHistory 실행 중');
      return true;
    }
    const selNetwork = await getBlockChain();
    const telegramId = await getTelegramId();

    if (!selNetwork) {
      console.error('syncAllTransaction: no network');
      return false;
    }
    if (!telegramId) {
      console.error('syncAllTransaction: no telegramId');
      return false;
    }

    try {
      // 동작중 flag 변경
      setIsRunAllTransactionHistory(true);

      // stop state reset
      setReqTxStop(false);

      // DB TX 동기화 정보 표시 초기화
      setCountSyncProcess(0) // 진행한 갯수
      setCountSyncComplete(0) // 전체 갯수
      setLastSyncTxs([]) // 동기화 TX 전송내역

      setSyncStatus(SYNC_STATUS.CHECKING);

      // syncKey 생성
      const randomNumbers = Array.from({ length: 6 }, () => Math.floor(Math.random() * 10));
      const key = `sync${Date.now()}${randomNumbers.join('')}`;
      setSyncKey(key);

      // BscScan 에서 모든 네이티브 TX ID 가져오기
      let rpcTxIds = await getAllNativeTxIdForAddressFromBscScan(walletAddress, 'asc');
      console.log(`1-1 syncAllTransactionHistory walletAddress : ${walletAddress} rpcTxIds : `, rpcTxIds);

      // RPC 에서 토큰 BEP20 TX ID 가져오기
      // 검색할 토큰 컨트랙트는 defaultTokenList 에서 가져옴
      const networkTokens = await getNetworkTokenData(selNetwork);
      console.log(`1-2 syncAllTransactionHistory networkTokens : `, networkTokens);

      for (const token of networkTokens) {
        // 지정한 토큰의 txid 조회
        const txidList = await getAllBEP20TxIdForAddressFromBscScan(walletAddress, token.contract, 'asc');
        console.log(`1-3 syncAllTransactionHistory walletAddress : ${walletAddress}  token : ${token.symbol} contract : ${token.contract} txidList : `, txidList);
        // txidList를 rpcTxIds에 추가
        if (txidList && txidList.length > 0) {
          // rpcTxIds.push(...txidList);
          rpcTxIds = [...rpcTxIds, ...txidList];
        }

        // reqTxStop 체크
        if (reqTxStopRef.current) {
          setSyncStatus(SYNC_STATUS.STOP);
          return true;
        }
      }
      console.log(`1-4 syncAllTransactionHistory RPC rpcTxIds : `, rpcTxIds);

      if (!rpcTxIds || rpcTxIds.length === 0) {
        console.log('rpcTxIds 데이터가 없습니다. syncAllTransactionHistory 함수를 종료합니다.');
        setSyncStatus(SYNC_STATUS.COMPLETE);
        return true;
      }

      // slot 번호 기준으로 오름차순 정렬
      rpcTxIds.sort((a, b) => {
        if (a.slot && b.slot) { return a.slot - b.slot; }
        return 0;
      });
      console.log(`1-5 syncAllTransactionHistory slot 정렬된 rpcTxIds : `, rpcTxIds);


      // RPC 에서 보유한 코인 balance 정보를 가져온다
      const rpcCoinBalance = await getBalance(walletAddress, false);
      console.log(`2 syncAllTransactionHistory RPC coinBalance : `, rpcCoinBalance);

      // RPC 지정한 토큰 컨트랙트의 잔액 정보를 가져오기
      let tokenBalances: any[] = [];
      // 토큰 컨트랙트 별 잔액 정보 가져오기
      for (const token of networkTokens) {
        const balance = await getTokenBalance(walletAddress, token.contract, false);
        tokenBalances.push({ mintAddress: token.contract, balance: balance });
      }
      console.log(`3 syncAllTransactionHistory RPC tokenBalances : `, tokenBalances);

      // 서버에서 최신 tx id 데이터 10개를 가져온다
      const serverTxIds = await getServerTxIdList(walletAddress, selNetwork, telegramId, 1, 10);
      console.log(`4 syncAllTransactionHistory serverTxIds : `, serverTxIds);

      // 서버에서 코인 보유수량 리스트를 가져온다
      const mainCurrency = bscNetwork?.currency.toLowerCase() || 'bnb'
      const serverBalances = await getBalanceFromServer(walletAddress, mainCurrency, false);
      console.log(`5 syncAllTransactionHistory server Coin Balances : `, serverBalances);

      // 서버에서 토큰 보유수량 리스트를 가져온다
      const serverTokenBalances = await getBalanceListFromServer(walletAddress)
      console.log(`6 syncAllTransactionHistory server Token Balances : `, serverTokenBalances);

      // rpc - 서버 코인 수량 비교
      let rpcCoinMatch = false;
      if (rpcCoinBalance.toString() === serverBalances.toString()) {
        console.log('RPC와 서버의 코인 잔액이 일치합니다.');
        rpcCoinMatch = true;
      } else {
        console.warn(`RPC와 서버의 코인 잔액이 일치하지 않습니다. RPC: ${rpcCoinBalance}, 서버: ${serverBalances}`);
      }

      // rpc - 서버 토큰 비교
      const matchingTokens = tokenBalances.filter(token =>
        serverTokenBalances.some((serverToken: any) => serverToken.ta_mintaddress === token.mintAddress)
      )
      console.log('7-1 syncAllTransactionHistory 지갑서버 토큰 정보:', matchingTokens)
      console.log('7-2 syncAllTransactionHistory 일치하는 토큰 정보:', matchingTokens)

      // (일치하는 TX 있는지 비교) - 간단화
      // rpc의 최신 tx id 1개와 서버에서 가져온 최신 tx id 1개가 일치하는지 확인한다
      let rpcTxIdMatch = false;
      if (rpcTxIds != null && serverTxIds != null && rpcTxIds.length !== 0 && serverTxIds.length !== 0) {
        const rpcLatestTxId = rpcTxIds[rpcTxIds.length - 1];
        const serverLatestTxId = serverTxIds[0].transaction_hash;

        if (rpcLatestTxId.signature.toString() === serverLatestTxId.toString()) {
          console.log('RPC의 최신 tx id와 서버의 최신 tx id가 일치합니다.');
          rpcTxIdMatch = true;
        }
      }

      let rpcTokensMatch = true;
      if (!serverTokenBalances || serverTokenBalances.length === 0) {
        console.warn('서버의 토큰 보유수량 데이터가 없습니다.');
        rpcTokensMatch = false;
      } else if (matchingTokens.length > 0) {
        for (const token of matchingTokens) {
          const serverToken = serverTokenBalances.find((st: any) => st?.ta_mintaddress?.toString() === token.mintAddress.toString());

          console.log(`rpc 토큰 정보 :`, token);
          console.log(`서버 토큰 정보 :`, serverToken);

          console.log(`RPC - 서버 토큰 보유수량 비교 ${serverToken.ta_total_amount} | ${token.balance}`);
          if (!serverToken || token.balance.toString() !== serverToken.ta_total_amount.toString()) {
            console.warn(`토큰 ${token.mintAddress}의 잔액 ${token.balance} - ${serverToken.ta_total_amount} 이 일치하지 않습니다.`);
            rpcTokensMatch = false;
            break;
          }
        }
      } else {
        rpcTokensMatch = false;
        console.log(`rpc 토큰 정보 일치하지 않음 rpcTokensMatch : `, rpcTokensMatch);
      }

      if (rpcTxIdMatch && rpcCoinMatch && rpcTokensMatch) {
        console.log('RPC의 최신 tx id와 서버의 최신 tx id가 일치하며 코인, 토큰 잔액도 모두 일치합니다.');
        setSyncStatus(SYNC_STATUS.COMPLETE)
        return true;
      }

      // 일치하지 않으면 RPC 에서 가져온 tx id 리스트 중에 서버에서 가져온 최신 tx id가 포함되어 있는지 확인한다
      let newTxIds: any[] = [];

      if (!serverTxIds || serverTxIds.length === 0) {
        if (!rpcTxIds || rpcTxIds.length === 0) {
          console.log('RPC의 tx id 정보가 없음.');
          setSyncStatus(SYNC_STATUS.COMPLETE)
          return true;
        }
        newTxIds = rpcTxIds;
        console.log('serverTxIds가 없으므로 rpcTxIds를 그대로 사용.');
      } else {
        const serverLatestTxId = serverTxIds[0].transaction_hash;
        console.log(`serverLatestTxId : `, serverLatestTxId)
        const isIncluded = rpcTxIds.some((rpcTx: any) => rpcTx.signature === serverLatestTxId);

        if (isIncluded) {
          console.log('RPC의 tx id 리스트에 서버의 최신 tx id가 포함되어 있습니다.');

          // RPC tx id 최신 ~ 서버 tx id 최신 사이의 tx id를 추출한다
          const startIndex = rpcTxIds.findIndex((rpcTx: any) => rpcTx.signature.toString() === serverLatestTxId.toString());
          if (startIndex !== -1 && startIndex < rpcTxIds.length - 1) {
            // 서버 txid 위치 다음 ~ rpc 최신 txid 사이
            newTxIds = rpcTxIds.slice(startIndex + 1);
            console.log('추출된 새로운 tx id 리스트 1 :', newTxIds);
          }
        } else {
          console.log('RPC의 tx id 리스트에 서버의 최신 tx id가 포함되어 있지 않습니다.');

          const isIncludedInAdditional = rpcTxIds.some((tx: any) => tx.signature === serverLatestTxId);

          if (isIncludedInAdditional) {
            console.log('RPC에서 가져온 tx id 리스트에 서버의 최신 tx id가 포함되어 있습니다.');
            const startIndex = rpcTxIds.findIndex((tx: any) => tx.signature === serverLatestTxId);

            if (startIndex !== -1) {
              // 서버 txid 위치 ~ rpc 최신 txid 사이
              newTxIds = rpcTxIds.slice(startIndex + 1);
              console.log('추출된 새로운 tx id 리스트 2 :', newTxIds);
            } else {
              console.warn('RPC tx id 리스트에서 서버의 최신 tx id를 찾을 수 없습니다.');
              setSyncStatus(SYNC_STATUS.ERROR)
              return false;
            }
          } else {
            console.log('RPC에서 가져온 tx id 리스트에 서버의 최신 tx id가 포함되어 있지 않습니다.');
            setSyncStatus(SYNC_STATUS.ERROR)
            return false;
          }
        }
      }
      console.log('8 syncAllTransactionHistory 트랜잭션 상세 요청할 newTxIds:', newTxIds)

      // 3) 10개 이하면 syncLimitTransaction, 아니면 나눠서 처리
      if (0 === newTxIds.length) {
        console.log('9 syncAllTransactionHistory DB 현행화 할 tx 없음. 종료')
        setSyncStatus(SYNC_STATUS.COMPLETE)
        return true;
      }
      else if (0 < newTxIds.length && 10 >= newTxIds.length) {
        console.log('10 syncAllTransactionHistory DB 현행화 할 tx 데이터 10개 이하')
        await syncLimitTransaction(walletAddress, newTxIds, SYNC_STATUS.COMPLETE.toLowerCase())
        setSyncStatus(SYNC_STATUS.COMPLETE)
        return true;
      }
      else if (newTxIds.length > 10) {
        console.log('11 syncAllTransactionHistory DB 현행화 할 tx 데이터 10개 이상')
        const firstTxIds = newTxIds.slice(0, 10);
        newTxIds = newTxIds.slice(10); // 나머지 다시 저장

        const result = await syncLimitTransaction(walletAddress, firstTxIds, SYNC_STATUS.PROCESSING.toLowerCase())
        if (false === result) {
          console.log('12 syncAllTransactionHistory syncLimitTransaction 실행 오류')
          setSyncStatus(SYNC_STATUS.ERROR)
          return false;
        }

        console.log(`13 syncAllTransactionHistory syncLimitTransaction result : `, result)
      }

      setCountSyncComplete(newTxIds.length);

      // 추출한 tx 리스트의 트랜잭션 상세 정보를 순차적으로 가져와 서버에 추가한다
      let countProcess = 0;
      let dbTxSyncStatus = SYNC_STATUS.PROCESSING.toLowerCase();
      for (const txInfo of newTxIds) {
        if (txInfo) {
          setSyncStatus(SYNC_STATUS.PROCESSING)

          if (reqTxStopRef.current) {
            console.log(`############### syncAllTransaction reqTxStop 1-1`, reqTxStopRef.current)
            setSyncStatus(SYNC_STATUS.STOP)
            return true;
          }

          // txInfo 정보 없음
          if (!txInfo) {
            console.warn(`13. syncAllTransactionHistory tx or tx.transaction.message is null`)
            continue;
          }

          let parsedTransaction;

          // 트랜잭션의 데이터 파싱해서 서버에 저정할 데이터 생성
          // 네이티브 트랜잭션(normal, internal)
          // 토큰 트랜잭션

          // txInfo에 tokenName 또는 tokenSymbol 등이 있다면 토큰 트랜잭션으로 간주
          let symbol = bscNetwork?.currency.toLowerCase() || 'bnb';
          if (txInfo.tokenName || txInfo.tokenSymbol) {
            parsedTransaction = await parseTokenTransaction(txInfo, walletAddress);
            console.log(`14-1. syncLimitTransaction Token 트랜잭션 파싱 결과: `, parsedTransaction);
            symbol = parsedTransaction?.tokenSymbol?.toLowerCase() || 'beptoken';
          } else {
            parsedTransaction = await parseBnbTransaction(txInfo, walletAddress);
            console.log(`14-2. syncLimitTransaction 네이티브 트랜잭션 파싱 결과: `, parsedTransaction);
          }

          if (!parsedTransaction) {
            console.log(`15. syncLimitTransaction parsedTransaction 데이터 없음 `);
            continue;
          }

          // newTxIds의 마지막 인덱스 도달 여부 확인
          const isLastIndex = countProcess === (newTxIds.length - 1);
          dbTxSyncStatus = SYNC_STATUS.PROCESSING.toLowerCase();
          if (isLastIndex) {
            dbTxSyncStatus = SYNC_STATUS.COMPLETE.toLowerCase();
            console.log(`16 syncAllTransactionHistory 마지막 값 도달 DB TX 현행화 상태 설정 : `, dbTxSyncStatus)
          }

          console.log(`16. syncLimitTransaction symbol : `, symbol);

          const response = await addServerTxHistory(
            walletAddress,
            selNetwork,
            telegramId,
            parsedTransaction.hash,
            parsedTransaction.blockNumber.toString(),
            parsedTransaction.timestamp.toString(),
            symbol,
            parsedTransaction.mintAddress,
            parsedTransaction.from.hash,
            parsedTransaction.to.hash,
            parsedTransaction.amount.toString(),
            parsedTransaction.fee.value.toString(),
            parsedTransaction.rentFee.value.toString(),
            parsedTransaction.transfer_type,
            syncKeyRef.current,
            dbTxSyncStatus
          );

          if (true === response.result && response.data.length > 0) {
            // DB 현행화 진행 수량
            countProcess = countProcess + 1;
            setCountSyncProcess(countProcess)

            await updateLastTxTo(parsedTransaction, walletAddress, symbol);
          } else if (false === response.result &&
            (323 === response.error_code || 324 === response.error_code || 325 === response.error_code)) {
            console.warn(`10-3 syncAllTransactionHistory addServerTxHistory fail `, response.message)
            setSyncStatus(SYNC_STATUS.SYNCKEY_ERROR)
            return true;
          } else if (false === response.result && 301 === response.error_code) {
            console.warn(`10-4 syncAllTransactionHistory addServerTxHistory overlap `, response.message)
            setSyncStatus(SYNC_STATUS.STOP)
            return true;
          } else if (false === response.result) {
            console.warn(`10-4 syncAllTransactionHistory addServerTxHistory fail `, response.message)
            setSyncStatus(SYNC_STATUS.STOP)
            return true;
          }

        }
      }

      console.log('12 syncAllTransactionHistory DB 현행화 완료:')
      setSyncStatus(SYNC_STATUS.COMPLETE);
      return true;

    } catch (error) {
      console.error(`Error syncAllTransactionHistory : `, error)
      setSyncStatus(SYNC_STATUS.ERROR);
      setReqTxStop(false);
      return false;
    } finally {
      // 동작중 flag 초기화
      setIsRunAllTransactionHistory(false);
      setReqTxStop(false);
    }
  }

  /**
   * 10개 이하인 경우 트랜잭션 데이터 동기화
   * @param walletAddress 
   * @param transactions : 트랜잭션 정보
   * @param syncStatusStr 
   * @returns 
   */
  const syncLimitTransaction = async (
    walletAddress: string,
    transactions: any[],
    syncStatusStr: string
  ): Promise<any> => {
    if (runLimitTransactionHistoryRef.current) {
      console.log('이미 syncLimitTransaction 실행 중');
      return { items: [] };
    }
    try {
      // 동작중 flag 변경
      setIsRunLimitTransactionHistory(true);

      // tx 요청 중지 flag 초기화
      setReqTxStop(false);

      const selNetwork = await getBlockChain();
      const telegramId = await getTelegramId();

      if (!selNetwork) {
        console.error('Error syncLimitTransaction : network 설정값 없음.');
        return false; // 또는 적절한 에러 처리를 추가할 수 있습니다.
      }
      if (!telegramId) {
        console.error('Error syncLimitTransaction : telegramId 설정값 없음.');
        return false; // 또는 적절한 에러 처리를 추가할 수 있습니다.
      }

      console.log(`---------------syncLimitTransaction 시작`);
      console.log(`1. syncLimitTransaction transactions : `, transactions);

      // bsc는 transactions에 트랜잭션 상세정보가 포함되어서 전달됨
      // 파싱된 트랜잭션의 서버 저장
      for (const txInfo of transactions) {
        if (txInfo) {
          let parsedTransaction;

          // 트랜잭션의 데이터 파싱해서 서버에 저정할 데이터 생성
          // 네이티브 트랜잭션(normal, internal)
          // 토큰 트랜잭션

          // txInfo에 tokenName 또는 tokenSymbol 등이 있다면 토큰 트랜잭션으로 간주
          let symbol = bscNetwork?.currency.toLowerCase() || 'bnb';
          if (txInfo.tokenName || txInfo.tokenSymbol) {
            parsedTransaction = await parseTokenTransaction(txInfo, walletAddress);
            console.log(`2-1. syncLimitTransaction Token 트랜잭션 파싱 결과: `, parsedTransaction);
            symbol = parsedTransaction?.tokenSymbol?.toLowerCase() || 'beptoken';
          } else {
            parsedTransaction = await parseBnbTransaction(txInfo, walletAddress);
            console.log(`2-2. syncLimitTransaction 네이티브 트랜잭션 파싱 결과: `, parsedTransaction);
          }

          if (!parsedTransaction) {
            console.log(`3. syncLimitTransaction parsedTransaction 데이터 없음 `);
            continue;
          }

          console.log(`4. syncLimitTransaction symbol : `, symbol);

          const response = await addServerTxHistory(
            walletAddress,
            selNetwork,
            telegramId,
            parsedTransaction.hash,
            parsedTransaction.blockNumber.toString(),
            parsedTransaction.timestamp.toString(),
            symbol,
            parsedTransaction.mintAddress,
            parsedTransaction.from.hash,
            parsedTransaction.to.hash,
            parsedTransaction.amount.toString(),
            parsedTransaction.fee.value.toString(),
            parsedTransaction.rentFee.value.toString(),
            parsedTransaction.transfer_type,
            syncKeyRef.current,
            syncStatusStr
          );

          if (true === response.result && response.data.length > 0) {
            // DB 현행화 진행 수량
            await updateLastTxTo(parsedTransaction, walletAddress, symbol);
          } else if (false === response.result &&
            (323 === response.error_code || 324 === response.error_code || 325 === response.error_code)) {
            console.warn(`10-3 syncLimitTransaction addServerTxHistory fail `, response.message)
            setSyncStatus(SYNC_STATUS.SYNCKEY_ERROR)
            return true;
          } else if (false === response.result && 301 === response.error_code) {
            console.warn(`10-4 syncLimitTransaction addServerTxHistory overlap `, response.message)
          } else if (false === response.result) {
            console.warn(`10-4 syncLimitTransaction addServerTxHistory fail `, response.message)
            setSyncStatus(SYNC_STATUS.STOP)
            return true;
          }

        }
      }

      return true;
    } catch (error) {
      console.error(`Error syncLimitTransaction`, error)
    } finally {
      setIsRunLimitTransactionHistory(false);
      setReqTxStop(false);

      console.log(`종료 syncLimitTransaction`)
    }
    return false;
  };



  // ─────────────────────────────────────────────────────────────
  // 9) BNB 트랜잭션 파싱 (원래 getSolTxParseV2 등)
  //    여기서는 단순히 hash, from, to, value, blockNumber, timestamp parse
  // ─────────────────────────────────────────────────────────────
  /**
   * tx 정보를 파싱해서 서버에 저장할 데이터
   * @param tx : 트랜잭션 상세 정보
   * @param walletAddress 
   * @returns 
   */
  async function parseBnbTransaction(
    tx: any,
    walletAddress: string
  ) {
    try {
      console.log(`1. parseBnbTransaction walletAddress: ${walletAddress}`, tx);

      // 1) 토큰 전송인 경우(methodId="0xa9059cbb") → 파싱 제외
      //    (이 함수에서는 토큰 트랜잭션을 처리하지 않음)
      if (tx.methodId === '0xa9059cbb' || tx.functionName?.startsWith('transfer(')) {
        console.log('BEP20 토큰 전송 트랜잭션 → 제외');
        return null;
      }

      // 2) blockNumber, timeStamp (숫자형 변환)
      const slot = Number(tx.slot);
      const blockTime = Number(tx.timeStamp);

      // 3) from / to
      const fromAddr = (tx.from || '0x0000000000000000000000000000000000000000');
      const toAddr = (tx.to || '0x0000000000000000000000000000000000000000');

      // 4) value (BNB 전송량, wei 단위)
      const valueWei: BigNumber = BigNumber.from(tx.value);
      const valueBNB: string = ethers.utils.formatEther(valueWei);

      // 5) 수수료 계산: gasUsed * gasPrice (wei) → BNB
      const gasUsed: BigNumber = BigNumber.from(tx.gasUsed);
      const gasPrice: BigNumber = BigNumber.from(tx.gasPrice);
      const feeWei: BigNumber = gasUsed.mul(gasPrice);
      // 수수료를 BNB 단위로 표시하려면:
      // const feeBNB: string = ethers.utils.formatEther(feeWei);

      // 6) 출금/입금 구분
      //    - 만약 from가 내 지갑이면 → 'withdrawal'
      //    - 만약 to가 내 지갑이면   → 'receive'
      //    - 나머지는 빈 문자열('') 등
      let transfer_type: 'withdrawal' | 'receive' | '' = '';
      if (fromAddr.toLowerCase() === walletAddress.toLowerCase()) {
        transfer_type = 'withdrawal';
      } else if (toAddr.toLowerCase() === walletAddress.toLowerCase()) {
        transfer_type = 'receive';
      }


      // 7) parseData 생성
      const parseData = {
        // 트랜잭션 식별
        hash: tx.hash,          // 실제 트랜잭션 해시
        transaction_hash: tx.hash,
        blockNumber: slot,
        blockTime,
        timestamp: blockTime,

        // 주소/금액
        from: { hash: fromAddr },
        to: { hash: toAddr },
        amount: tx.value,        // wei 단위
        value: valueBNB,         // BNB 단위
        transfer_type,           // 'withdrawal' | 'receive' | ''

        // 가스 수수료 (wei로 저장)
        fee: { value: feeWei.toString() },
        rentFee: { value: '0' }, // rentFee는 0 처리
        mintAddress: '',         // 네이티브 BNB이므로 빈 문자열

        // 기타 정보
        input: tx.input,
        methodId: tx.methodId,
        functionName: tx.functionName
      };

      // 8) 컨트랙트 실행 + BNB 전송량이 0인 경우
      //    → to가 내 지갑주소가 아닐 수도 있으나, from이 내 지갑이면
      //       수수료만 출금된 트랜잭션으로 간주 가능
      if (parseData.value === '0.0' && transfer_type === '' && fromAddr === walletAddress.toLowerCase()) {
        // 'withdrawal'(수수료만 나감)
        parseData.transfer_type = 'withdrawal';
      }

      console.log('parseBnbTransaction result:', parseData);
      return parseData;
    } catch (error) {
      console.error('parseBnbTransaction error:', error);
      return null;
    }
  }

  /**
   * 토큰 트랜잭션 파싱
   * @param tx 
   * @param walletAddress 
   */
  async function parseTokenTransaction(
    tx: any,
    walletAddress: string
  ) {
    try {
      console.log(`1. parseTokenTransaction walletAddress : ${walletAddress} | tx : `, tx);

      // 1. 블록번호(slot)와 타임스탬프(blockTime) 추출
      const slot = Number(tx.slot);
      const blockTime = Number(tx.timeStamp);

      // 2. 보내는 주소와 받는 주소
      const fromAddr = (tx.from || 'none');
      const toAddr = (tx.to || 'none');

      // 3. 전송 수량(amount): tx.value는 토큰의 최소 단위(wei와 유사)
      const amount = tx.value; // 그대로 문자열로 사용

      // 토큰의 소수점 자리수는 tx.tokenDecimal (문자열) → Number로 변환
      const decimals = Number(tx.tokenDecimal);

      // human-readable 값 (예, 토큰 단위)로 변환: ethers.utils.formatUnits
      const value = ethers.utils.formatUnits(amount, decimals);

      // 4. 수수료(fee) 계산: gasUsed와 gasPrice를 곱해 wei 단위 수수료를 구한 뒤,
      //    ethers.utils.formatEther를 사용해 BNB 단위 문자열로 변환
      const gasUsed: BigNumber = BigNumber.from(tx.gasUsed);
      const gasPrice: BigNumber = BigNumber.from(tx.gasPrice);
      const feeWei: BigNumber = gasUsed.mul(gasPrice);
      // const feeBNB: string = ethers.utils.formatEther(feeWei);

      // 5. 출금/입금 구분: 만약 보내는 주소(fromAddr)가 주어진 walletAddress와 같으면 'withdrawal'(출금),
      //    아니면 'receive'(입금)으로 처리 (비교 시 모두 소문자로 변환)
      let transfer_type = '';
      if (fromAddr.toLowerCase() === walletAddress.toLowerCase()) {
        transfer_type = 'withdrawal'
      } else if (toAddr.toLowerCase() === walletAddress.toLowerCase()) {
        transfer_type = 'receive'
      }

      // 6. 네이티브 토큰(여기서는 BEP-20 토큰) 거래이므로 mintAddress는 tx에 저장된 토큰 컨트랙트 주소
      const networkSymbol = bscNetwork?.symbol.toLowerCase() || 'bsc'
      const mintAddress = tx.mintAddress;
      const tokenInfo = await getNetworkTokenDataByAddress(networkSymbol, mintAddress);
      console.log(`2. parseTokenTransaction tokenInfo : `, tokenInfo);

      let tokenSymbol;
      if (tokenInfo && tokenInfo.symbol) {
        tokenSymbol = tokenInfo.symbol.toString();
      } else {
        tokenSymbol = tx.tokenSymbol.toString();
      }

      // 7. 최종 파싱된 데이터 객체 생성
      const parseData = {
        hash: tx.signature,
        blockNumber: slot,                // 블록번호
        blockTime: blockTime,             // 타임스탬프
        from: { hash: fromAddr },         // 보내는 주소
        to: { hash: toAddr },             // 받는 주소
        amount: amount,                   // 보낸 수량 (wei 단위)
        value: value,                     // 보낸 수량 (토큰 단위)
        fee: { value: feeWei },           // 전송 수수료 (wei 단위)
        rentFee: { value: '0' },          // rentFee는 0
        timestamp: blockTime,             // 타임스탬프와 동일
        transaction_hash: tx.hash,        // 트랜잭션 해시
        mintAddress: mintAddress,         // 토큰 컨트랙트 주소
        transfer_type: transfer_type,     // 출금/입금 구분
        // 추가 정보 (필요 시)
        tokenName: tx.tokenName,
        tokenSymbol: tokenSymbol,
        tokenDecimal: tx.tokenDecimal,
        // 필요에 따라 다른 필드들도 추가 가능
        transactionIndex: tx.transactionIndex,
        gas: tx.gas,
        gasPrice: tx.gasPrice,
        gasUsed: tx.gasUsed,
        cumulativeGasUsed: tx.cumulativeGasUsed,
        input: tx.input,
        confirmations: tx.confirmations
      };

      console.log(`2. parseTokenTransaction - parseData : `, parseData);

      return parseData;
    } catch (error) {
      console.error("parseTokenTransaction error:", error);
      return null;
    }
  }

  /**
   * server에 tx 데이터 추가 요청
   * 
   * @param walletAddress 
   * @param network 
   * @param tid 
   * @param txId 
   * @param blockNum 
   * @param timeStamp 
   * @param coinName 
   * @param from 
   * @param to 
   * @param amount 
   * @param fee 
   * @param rentFee 
   * @param transferType 
   * @param syncKey 
   * 
   * @returns 
   */
  const addServerTxHistory = async (
    walletAddress: string,
    network: string,
    tid: string,
    txId: string,
    blockNum: string,
    timeStamp: string,
    coinName: string,
    mintAddress: string,
    from: string,
    to: string,
    amount: string,
    fee: string,
    rentFee: string,
    transferType: string,
    syncKey: string,
    syncStatus: string
  ): Promise<any | null> => {
    try {
      // WASM 함수 호출
      const response = await add_server_tx_history(
        process.env.REACT_APP_QWALLET_SERVER_URL!,
        walletAddress,
        network,
        tid,
        txId,
        blockNum.toString(),
        timeStamp.toString(),
        coinName,
        mintAddress,
        ethers.utils.getAddress(from),
        ethers.utils.getAddress(to),
        amount.toString(),
        fee.toString(),
        rentFee.toString(),
        transferType,
        syncKey,
        syncStatus
      );
      console.log(`response`, response)

      const result = JSON.parse(response)
      console.log(`JSON.parse result`, result)

      return result
    } catch (error) {
      console.error('qWallet 서버에서 tx 데이터 추가 중 오류 발생:', error);
      // throw error;
      return null
    }
  }

  /**
   * tx id 리스트 요청
   * 
   * @param walletAddress 
   * @param network 
   * @param tid 
   * @param limit 
   * @returns 
   */
  const getServerTxIdList = async (
    walletAddress: string,
    network: string,
    tid: string,
    page: number = 1,
    take: number = 5
  ): Promise<any | null> => {
    try {
      if (!walletAddress || !network || !tid) {
        console.error('유효하지 않은 파라미터: walletAddress, network, tid는 필수입니다.');
        return null;
      }

      const result = await get_server_tx_id_list(
        process.env.REACT_APP_QWALLET_SERVER_URL!,
        walletAddress,
        network,
        tid,
        page,
        take,
      );
      console.log(`getServerTxIdList result`, result)

      const response = JSON.parse(result)
      console.log(`JSON.parse response`, response)

      // key name 변경
      if (true === response.result && response.data.length > 0) {
        const transformedData = response.data.map((item: any) => ({
          slot: item.th_blocknum,
          transaction_hash: item.th_txid
        }));

        return transformedData
      } else {
        return null;
      }
    } catch (error) {
      console.error('qWallet 서버에서 tx id 리스트 요청 중 오류 발생:', error);
      throw error;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 10) 마지막 Tx를 전송내역 형태로 추가
  // ─────────────────────────────────────────────────────────────
  const updateLastTxTo = async (tx: any, accountAddress: string, symbol: string) => {
    const txId = tx.transaction_hash;
    const fromAddress = tx.from.hash;
    const detailDate = moment(tx.timestamp * 1000).format('YYYY. M. D');

    // BSC에서는 amount = rawWei. Solana 때와 달리 decimals은 tokenInfo.decimals 이용
    let amount = '0';
    if (tx.mintAddress) {
      // ERC-20
      // tx.amount가 wei로 들어있으므로 decimals로 나눈다
      const decimals = tx.tokenDecimal;
      const valBN = ethers.BigNumber.from(tx.amount);
      amount = ethers.utils.formatUnits(valBN, decimals);
    } else {
      // BNB
      const valBN = ethers.BigNumber.from(tx.amount);
      amount = ethers.utils.formatEther(valBN);
    }

    symbol = symbol.substring(0, 4);

    const txData: SyncTx = {
      id: `${txId}`,
      type:
        fromAddress.toLowerCase() === accountAddress.toLowerCase()
          ? i18next.t('template_wallet_screen_message_09') // 출금
          : i18next.t('template_wallet_screen_message_10'), // 입금
      amount: amount,
      currency: symbol.toLocaleUpperCase(),
      date: detailDate,
      direction: fromAddress.toLowerCase() === accountAddress.toLowerCase() ? 'out' : 'in'
    };

    setLastSyncTxs((prev) => {
      const updated = [txData, ...prev];
      if (updated.length > 3) {
        updated.pop();
      }
      return updated;
    });
    return txData;
  };

  // ─────────────────────────────────────────────────────────────
  // 11) 토큰 정보 조회(ERC-20)
  // ─────────────────────────────────────────────────────────────
  const getTokenInfo = async (mintAddress: string) => {
    try {

      if (!provider) {
        console.error('provider is not initialized');
        return null;
      }

      const contract = new ethers.Contract(mintAddress, ERC20_ABI, provider);
      const decimals = await contract.decimals();
      // name(), symbol() API가 ABI에 있어야 호출 가능
      // 여기서는 임시로
      const name = await contract.name();
      const symbol = await contract.symbol();

      return {
        name,
        symbol,
        decimals
      };
    } catch (error) {
      console.error('getTokenInfo error:', error);
      return null;
    }
  };

  /**
   * qWallet 서버에서 마지막 3개 tx data 요청
   * @param walletAddress : 지갑 주소
   * @param network : 네트워크 종류
   * @param tid : 텔레그럄 id
   * @param tokenName : 토큰 이름
   * @param page : 페이지 번호
   * @param take : 페이지당 표시할 데이터 수
   * 
   * @returns jsonArray
   */
  const getServerTxHistory =
    async (
      walletAddress: string,
      network: string,
      tid: string,
      tokenName: string,
      page: number = 1,
      take: number = 5
    ): Promise<any | null> => {
      try {
        const result = await get_server_tx_history(
          process.env.REACT_APP_QWALLET_SERVER_URL!,
          walletAddress,
          network,
          tid,
          tokenName,
          page,
          take
        );

        console.log(`getServerTxHistory result`, result)

        const response = JSON.parse(result)
        console.log(`JSON.parse response`, response)

        // key name 변경
        if (true === response.result && response.data.length > 0) {

          console.log(`response.data`, response.data)

          const transformedData = response.data.flatMap((item: any) => {
            const baseTransaction = {
              slot: Number(item.th_blocknum),
              blockTime: item.th_timestamp,
              from: { hash: ethers.utils.getAddress(item.th_from) },
              to: { hash: ethers.utils.getAddress(item.th_to) },
              amount: item.th_amount,
              value: (item.th_amount / Math.pow(10, item.tokenInfo?.ti_decimal)).toString(),
              fee: {
                value: (item.th_fee / Math.pow(10, 18)).toString(),
              },
              rentFee: {
                value: item.th_rentfee
              },
              timestamp: item.th_timestamp,
              transaction_hash: item.th_txid,
              coin_type: item.tokenInfo?.ti_type,
              coin_name: item.th_coinname,
              seller: {
                seller_name: item.seller_name,
                qpin: item.qpin,
                seller_wallet: item.seller_wallet
              }
            };

            // 동일한 지갑 주소인 경우 (자체 전송)
            if (item.th_from.toString().toLowerCase() === item.th_to.toString().toLowerCase()) {
              return [
                {
                  ...baseTransaction,
                  transfer_type: 'receive'
                },
                {
                  ...baseTransaction,
                  transfer_type: 'withdrawal'
                }
              ];
            }

            // 일반적인 전송인 경우
            return [{
              ...baseTransaction,
              transfer_type: item.th_transfertype
            }];
          });

          return { items: transformedData }
        } else {
          return { items: [] }
        }
      } catch (error) {
        console.error('qWallet 서버에서 tx 데이터 요청 중 오류 발생:', error);
        return { items: [] }
      }
    }

  /**
    * 블록 익스플로러 url
    * @param chainName 
    * @param address 
    */
  const getBlockScanUrl =
    async (
      txid: string
    ): Promise<string> => {
      const blockExplorerUrl = bscNetwork?.blockexp || '';
      return `${blockExplorerUrl}/tx/${txid}`;
    }

  /**
   * address 유효성 검사
   * @param address 
   * @returns 
   */
  const isValidAddress = async (address: string): Promise<boolean> => {
    try {
      if (address) {
        // eth 지갑 형식
        return ethers.utils.isAddress(address);
      }
      return false; // address가 제공되지 않거나 다른 네트워크일 경우 기본적으로 false 반환
    } catch (error) {
      console.error(`BscContext isValidAddress error : ${error}`);
      return false; // 예외가 발생하면 false 반환
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Provider 리턴
  // ─────────────────────────────────────────────────────────────
  return (
    <BscContext.Provider
      value={{
        networkName,
        balance,
        countSyncProcess,
        countSyncComplete,
        syncStatus,
        lastSyncTxs,
        reqTxStop,
        startDbSyncTimer,
        stopDbSyncTimer,
        getBalance,
        getBalanceFromServer,
        getTokenBalance,
        getTokenBalanceFromServer,
        estimateGas,
        sendTransactionWithPrivateKey,
        getTotalTxCount,
        stopRequestTxData,
        syncAllTransaction,
        getTokenInfo,
        getServerTxHistory,
        getBlockScanUrl,
        isValidAddress
      }}
    >
      {children}
    </BscContext.Provider>
  );
};
